/**
 * Shared instrument primitives. Every state indicator renders colour **plus**
 * a glyph, a plain-language label and, where the fact is operational, its
 * freshness — the state contract requires all four.
 */
import type { ReactNode } from 'react';
import type {
  CapacityLayerView,
  ConfidenceLevel,
  DataQuality,
  Freshness,
  Provenance,
  SemanticTone,
} from '@dcc/shared';
import { PROVENANCE, PROVENANCE_TONE } from '@dcc/shared';
import { TONE_GLYPH, toneFor, type ToneKind, stateLabel } from '../lib/tone';
import { absoluteTime, freshnessLabel, percent, relativeTime } from '../lib/format';

export function Panel({
  title,
  subtitle,
  actions,
  children,
  flush,
  tone,
  as: Tag = 'section',
  labelledBy,
}: {
  title?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  flush?: boolean;
  tone?: SemanticTone;
  as?: 'section' | 'div' | 'article';
  labelledBy?: string;
}) {
  return (
    <Tag
      className={`panel${flush ? ' panel--flush' : ''}`}
      data-tone={tone}
      aria-labelledby={labelledBy}
    >
      {(title || actions) && (
        <header
          className={`panel__head${flush ? ' panel--flush-head' : ''}`}
          style={flush ? { padding: '16px 16px 0' } : undefined}
        >
          <div>
            {title && <h2 className="panel__title">{title}</h2>}
            {subtitle && <div className="panel__sub">{subtitle}</div>}
          </div>
          {actions && <div className="inline inline--tight">{actions}</div>}
        </header>
      )}
      {children}
    </Tag>
  );
}

export function StateChip({
  kind,
  state,
  label,
  size = 'sm',
  live = false,
}: {
  kind: ToneKind;
  state: string;
  label?: string;
  size?: 'sm' | 'lg';
  live?: boolean;
}) {
  const tone = toneFor(kind, state);
  const text = label ?? stateLabel(state);
  return (
    <span className={`chip${size === 'lg' ? ' chip--lg' : ''}`} data-tone={tone}>
      <span className="chip__glyph" aria-hidden="true">
        {TONE_GLYPH[tone]}
      </span>
      {live && <span className="dot dot--live" aria-hidden="true" />}
      {text}
    </span>
  );
}

export function ToneChip({ tone, children }: { tone: SemanticTone; children: ReactNode }) {
  return (
    <span className="chip" data-tone={tone}>
      <span className="chip__glyph" aria-hidden="true">
        {TONE_GLYPH[tone]}
      </span>
      {children}
    </span>
  );
}

export function Meter({
  value,
  tone,
  label,
  ariaLabel,
}: {
  value: number | null;
  tone: SemanticTone;
  label?: string;
  ariaLabel: string;
}) {
  const known = value !== null && Number.isFinite(value);
  const width = known ? Math.max(0, Math.min(100, value)) : 100;
  return (
    <div
      className={`meter${known ? '' : ' meter--unknown'}`}
      data-tone={tone}
      role="meter"
      aria-label={ariaLabel}
      aria-valuenow={known ? Math.round(width) : undefined}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuetext={
        known ? `${Math.round(width)}%${label ? ` ${label}` : ''}` : 'data unavailable'
      }
    >
      {known && <div className="meter__fill" style={{ width: `${width}%` }} />}
    </div>
  );
}

/** A value that the provider did not report. Never a zero, never a dash. */
export function Unknown({ why }: { why?: string }) {
  return (
    <span className="unknown" title={why ?? 'This value was not reported by the source.'}>
      data unavailable
    </span>
  );
}

export function FreshnessTag({ freshness, prefix }: { freshness: Freshness; prefix?: string }) {
  const tone: SemanticTone = freshness.isStale ? 'attention' : 'neutral';
  return (
    <span className="freshness" data-tone={tone} title={absoluteTime(freshness.observedAt)}>
      <span className={freshness.isStale ? 'stale-mark' : ''} aria-hidden="true">
        {freshness.isStale ? '!' : '·'}
      </span>
      {prefix ? `${prefix} ` : ''}
      {freshnessLabel(freshness)}
      {freshness.isStale && <span className="stale-mark"> stale</span>}{' '}
      <ProvenanceTag quality={freshness.quality} source={freshness.source} />
    </span>
  );
}

/**
 * Where a number came from, stated beside the number itself.
 *
 * This exists so a reader never has to assume. A burn rate DCC calculated and a
 * burn rate a provider measured look identical as digits; only this says which
 * one is on screen, and nothing but a provider adapter can make it read Direct.
 */
export function ProvenanceTag({
  quality,
  source,
  calculationVersion,
}: {
  quality: DataQuality;
  source?: string | null;
  calculationVersion?: string | null;
}) {
  const descriptor = PROVENANCE[quality];
  const tone = PROVENANCE_TONE[quality];
  const title = [
    descriptor.meaning,
    source ? `Source: ${source}` : null,
    calculationVersion ? `Calculation: ${calculationVersion}` : null,
  ]
    .filter(Boolean)
    .join('\n');
  return (
    <span className="chip chip--provenance" data-tone={tone} data-quality={quality} title={title}>
      <span className="chip__glyph" aria-hidden="true">
        {TONE_GLYPH[tone]}
      </span>
      {descriptor.label}
    </span>
  );
}

/** Convenience wrapper for the common case where a whole Provenance is at hand. */
export function ProvenanceOf({ provenance }: { provenance: Provenance }) {
  return (
    <ProvenanceTag
      quality={provenance.quality}
      source={provenance.source}
      calculationVersion={provenance.calculationVersion ?? null}
    />
  );
}

export function Confidence({
  confidence,
  percentValue,
  reasons,
}: {
  confidence: ConfidenceLevel;
  percentValue: number | null;
  reasons?: string[];
}) {
  const tone = toneFor('confidence', confidence);
  return (
    <span
      className="chip"
      data-tone={tone}
      title={reasons && reasons.length > 0 ? reasons.join('\n') : undefined}
    >
      <span className="chip__glyph" aria-hidden="true">
        {TONE_GLYPH[tone]}
      </span>
      {confidence === 'UNKNOWN'
        ? 'confidence unknown'
        : `${confidence.toLowerCase()} confidence${percentValue !== null ? ` · ${percentValue}%` : ''}`}
    </span>
  );
}

/** One capacity layer, rendered so the layers can visibly disagree. */
export function CapacityLayer({
  layer,
  title,
  compact = false,
}: {
  layer: CapacityLayerView;
  title: string;
  compact?: boolean;
}) {
  const tone = toneFor('capacity', layer.state);
  const remaining = layer.remainingPercent;
  return (
    <div className="stack stack--tight" data-tone={tone}>
      {/* In the compact variant the chip sits under the label: side by side it
          wraps badly inside a narrow tile column. */}
      <div className={compact ? 'stack stack--tight' : 'spread'}>
        <span className="label">{title}</span>
        <span>
          <StateChip kind="capacity" state={layer.state} />
        </span>
      </div>
      <div className="inline" style={{ gap: 8, alignItems: 'baseline' }}>
        <span className="stat__value" style={{ fontSize: compact ? 20 : 24 }}>
          {remaining === null ? <Unknown why={layer.provenance.source} /> : percent(remaining)}
        </span>
        {remaining !== null && <span className="stat__unit">remaining</span>}
      </div>
      <Meter value={remaining} tone={tone} label="remaining" ariaLabel={`${title} remaining`} />
      <div className="tiny dim">
        {layer.resetsAt && <>resets {relativeTime(layer.resetsAt)}</>}
        {layer.burnPercentPerHour !== null && <> · burn {layer.burnPercentPerHour}%/h</>}
        {layer.projectedExhaustionAt && layer.state === 'PROJECTED_SHORT' && (
          <> · runs out {relativeTime(layer.projectedExhaustionAt)}</>
        )}
      </div>
      <div className="inline inline--tight">
        <ProvenanceOf provenance={layer.provenance} />
        {!compact && <span className="tiny dim">{layer.provenance.source}</span>}
      </div>
    </div>
  );
}

export function Stat({
  label,
  value,
  unit,
  note,
  tone,
  large,
}: {
  label: string;
  value: ReactNode;
  unit?: string;
  note?: ReactNode;
  tone?: SemanticTone;
  large?: boolean;
}) {
  return (
    <div className="stat" data-tone={tone}>
      <span className="label">{label}</span>
      <span
        className={`stat__value${large ? ' stat__value--lg' : ''}`}
        style={tone ? { color: 'var(--tone)' } : undefined}
      >
        {value}
        {unit && <span className="stat__unit"> {unit}</span>}
      </span>
      {note && <span className="stat__note">{note}</span>}
    </div>
  );
}
